// Shows a pending human approval action.

import { useRef, useState } from 'react';
import { Check, Hand, PenLine, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Astroid } from './icons';
import { ConfidencePill, SourcePopover, SourcesRow } from './response-card';
import { sourceAnchorFor, type SourceAnchor } from './source-anchor';
import type { PendingAction } from './types';

interface ActionCardProps {
  action: PendingAction;
  armed: boolean;
  onArm: () => void;
  onConfirm: () => void;
  /** Hand the ticket back. Omitted where there is no ticket to hand back, which hides the button. */
  onReject?: () => void;
  /** Open the AI overlay to say what Kim should do instead. */
  onRejectAndDraft: () => void;
}

export function ActionCard({ action, armed, onArm, onConfirm, onReject, onRejectAndDraft }: ActionCardProps) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [openSource, setOpenSource] = useState<SourceAnchor | null>(null);
  return (
    <div
      ref={cardRef}
      className="relative flex w-full flex-col gap-4 rounded-2xl border border-accent-purple/50 bg-white/80 p-4 shadow-[0_4px_20px_rgba(147,15,255,0.07)] backdrop-blur-[6px]"
    >
      {openSource !== null && action.sources[openSource.index] && (
        <SourcePopover source={action.sources[openSource.index]} anchor={openSource} onClose={() => setOpenSource(null)} />
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Astroid filled className="size-[13px] text-purple-500" />
          <span className="font-mono text-[12px] font-medium uppercase leading-4 text-text-disabled">{action.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Highlight matches the category-label purple from the ticket list. */}
          <span className="flex items-center gap-1.5 rounded-full bg-accent-purple/10 px-2.5 py-1 text-[12px] font-medium leading-4 text-accent-purple">
            <Hand className="size-3" />
            Needs your approval
          </span>
          {action.confidencePct > 0 && <ConfidencePill pct={action.confidencePct} />}
        </div>
      </div>

      <p className="text-[13px] leading-[18px] text-text-body">{action.description}</p>

      <SourcesRow
        sources={action.sources}
        onOpen={(index, chip) => setOpenSource(sourceAnchorFor(cardRef.current, chip, index))}
      />

      {/* Reject sits at the far left; the draft option sits beside the main action. Wraps on narrow screens. */}
      <div className={cn('flex w-full flex-wrap items-center gap-3', onReject ? 'justify-between' : 'justify-end')}>
        {onReject && (
          <Button variant="ghost" onClick={onReject} className="text-[13px] font-normal text-gray-900">
            <X className="size-[14px]" />
            Reject &amp; Unassign
          </Button>
        )}
        <div className="flex flex-wrap items-center justify-end gap-3">
          {armed && (
            <p className="text-[13px] leading-[18px] text-text-body">Are you sure you want to perform the action?</p>
          )}
          {!armed && (
            <Button
              variant="outline"
              onClick={onRejectAndDraft}
              className="text-[13px] font-normal"
            >
              <PenLine className="size-[14px]" />
              Reject &amp; Draft response
            </Button>
          )}
          <Button
            onClick={armed ? onConfirm : onArm}
            className={cn(
              'text-[13px] font-normal',
              // Destructive pill per Figma: red fill with an offset light-red halo ring.
              armed && 'rounded-full border-red-500 bg-red-500 ring-1 ring-red-500 ring-offset-2 hover:bg-red-600',
            )}
          >
            <Check className="size-[14px]" />
            {action.ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
